import * as React from 'react';
import { IDashCannedResponse, DashCannedResponseApi } from "@nkr/models/dash/dash-canned-response";
import { Sidebar } from 'ClientApp/components/layout/sidebar';
import { useForm, UseFormReturn, UseSearchReturn } from 'ClientApp/utils/rest-api';
import { FormControl, Button, Row, Col, FormGroup, ControlLabel, ToggleButtonGroup, ToggleButton, Well, Alert, Label, Modal } from 'react-bootstrap';
import { TypeParser } from '@nkr/utils/form';
import { DashCannedResponseType } from '@nkr/models/dash/dash-enums';


interface IProps {
    selected        : IDashCannedResponse | null,
    setSelectedRow  : (selected: IDashCannedResponse | null) => void,
}

export const DashCannedResponsesSidebar = (props: IProps) => {
    if (!props.selected) return null;

    return (
        <Sidebar title='Canned Response' isOpen={true} onClose={() => props.setSelectedRow(null)}>
            <SidebarBody selected={props.selected} setSelectedRow={props.setSelectedRow} />
        </Sidebar>
    );
}

const SidebarBody = React.memo((props: IProps) => {
    const data            = props.selected as IDashCannedResponse;
    const form            = useForm<IDashCannedResponse>(DashCannedResponseApi, data.id || null);
    const deleteHook      = DashCannedResponseApi.useDelete( data.id || null );
    const defaultContent  = `Hi {CandidateFirstName},\r\n\r\nThank you for registering to be a potential living donor.\r\n\r\n\r\n\r\nWe appreciate your generosity and interest in giving others the opportunity for a healthier life.\r\n\r\nKind regards,\r\n{UserFirstName}`;

    form.setOnSaveComplete( ( data : IDashCannedResponse ) => {
        props.setSelectedRow(data);
    } );

    const deleteRecord = async () => {
        if (data.id) {
            if (window.confirm('The canned response will be permanently deleted. Are you sure you wish to continue?')) {

                const res = await deleteHook.submit();
                if( res.isSuccess ) {
                    props.setSelectedRow(null);
                }
            }
        }
    };

    const surveyName    = 'Donor Follow-Up Survey';
    const surveySubject ='Kidney Donation Follow Up Survey';
    const surveyContent = `Dear {CandidateFirstName},\r\n\r\nIt is approaching the {SurveyInstance} of your kidney donation. We hope that all is well. To improve the quality of care for donors and to better understand the outcomes of kidney donation, we kindly ask that you fill out a follow-up survey.\r\n\r\nTo fill out the survey {SurveyLink}.`;

    const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
    const textareaRef = React.useRef<HTMLTextAreaElement>(null);

    const insertToken = (token: string) => {
        const el = textareaRef.current;
        if (!el) return;
        const start = el.selectionStart ?? 0;
        const end   = el.selectionEnd ?? 0;
        const cur   = form.data.emailContent ?? '';
        form.setField('emailContent', cur.slice(0, start) + token + cur.slice(end));
        requestAnimationFrame(() => {
            el.selectionStart = start + token.length;
            el.selectionEnd   = start + token.length;
            el.focus();
        });
    };

    const onTypeChange = (e : React.FormEvent<unknown>) => {
        form.handleIntChange(e);

        const newType   = Number((e.target as HTMLSelectElement).value);
        const isNew     = !form.data.id;

        if (!isNew) return;

        if (newType === DashCannedResponseType.DonorFollowUpSurvey && !form.data.name && !form.data.emailSubject && !form.data.emailContent) {
            form.setField('name', surveyName);
            form.setField('emailSubject', surveySubject);
            form.setField('emailContent', surveyContent);
        }
        else if (newType === DashCannedResponseType.General && form.data.name === surveyName && form.data.emailSubject === surveySubject && form.data.emailContent === surveyContent) {
            form.setField('name', '');
            form.setField('emailSubject', '');
            form.setField('emailContent', '');
        }
    };

    const onSubmit = (e : React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if( !form.data.emailContent )
        {
            setErrorMessage('Please update the Email Content.');
            return false;
        }
        if (form.data.type === DashCannedResponseType.DonorFollowUpSurvey) {
            const missing = ['{CandidateFirstName}', '{SurveyInstance}', '{SurveyLink}']
                .filter(t => !(form.data.emailContent ?? '').includes(t));
            if (missing.length > 0) {
                setErrorMessage(`Email Content is missing required tokens: ${missing.join(', ')}`);
                return false;
            }
        }
        form.handleSubmit(e);
    }

    return (
        <>
            <Modal show={!!errorMessage} onHide={() => setErrorMessage(null)}>
                <Modal.Header closeButton>
                    <Modal.Title>Validation Error</Modal.Title>
                </Modal.Header>
                <Modal.Body>{errorMessage}</Modal.Body>
                <Modal.Footer>
                    <Button onClick={() => setErrorMessage(null)}>OK</Button>
                </Modal.Footer>
            </Modal>

            <Well bsSize="small">
                {!form.isLoaded && "Loading..."}

                {form.isLoaded && form.data && (
                <form onSubmit={onSubmit}>
                    <Row style={{ marginBottom: 10 }}>
                        <Col xs={12}><strong>Type:</strong></Col>
                        <Col xs={12}>
                            <FormControl componentClass='select' name='type' value={form.data.type ?? DashCannedResponseType.General} onChange={onTypeChange}>
                                <option value={DashCannedResponseType.General}>General</option>
                                <option value={DashCannedResponseType.DonorFollowUpSurvey}>Donor Follow-Up Survey</option>
                            </FormControl>
                        </Col>
                    </Row>
                    <Row style={{ marginBottom: 10 }}>
                        <Col xs={12}><strong>Name:</strong></Col>
                        <Col xs={12}><FormControl required name='name' type="text" placeholder="e.g. Contact attempts exhausted email" value={form.data.name ?? ''} onChange={form.handleStringChange} /></Col>
                    </Row>
                    <Row style={{ marginBottom: 10 }}>
                        <Col xs={12}><strong>Email Subject:</strong></Col>
                        <Col xs={12}><FormControl required name='emailSubject' type="text" placeholder="e.g. We were unable to contact you" value={form.data.emailSubject ?? ''} onChange={form.handleStringChange} /></Col>
                    </Row>
                    <Row style={{ marginBottom: 10 }}>
                        <Col xs={12}><strong>Email Content:</strong></Col>
                        {form.data.type === DashCannedResponseType.DonorFollowUpSurvey && (
                            <Col xs={12} style={{ marginBottom: 4, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 4 }}>
                                <small className='text-muted'>Insert token: </small>
                                {['{CandidateFirstName}', '{SurveyInstance}', '{SurveyLink}'].map(token => (
                                    <Label key={token} style={{ cursor: 'pointer', marginRight: 4 }} onClick={() => insertToken(token)}>{token}</Label>
                                ))}
                                <Button bsSize='xsmall' bsStyle='default' style={{ marginLeft: 'auto' }} onClick={() => {
                                    form.setField('name', surveyName);
                                    form.setField('emailSubject', surveySubject);
                                    form.setField('emailContent', surveyContent);
                                }}>Reset to default</Button>
                            </Col>
                        )}
                        <Col xs={12}><FormControl required componentClass='textarea' inputRef={(ref: any) => { textareaRef.current = ref; }} name='emailContent' type="text" value={form.data.emailContent ?? defaultContent} onChange={form.handleStringChange} style={{height: 200}} /></Col>
                    </Row>
                    <Row>
                        <Col xs={6}><Button bsStyle="primary" type='submit'>{form.data.id > 0 ? 'Update' : 'Create'}</Button></Col>
                        {form.data.id && (
                            <Col xs={6} style={{ textAlign: 'right' }}><Button bsStyle="danger" onClick={deleteRecord}>Delete</Button></Col>
                        )}
                    </Row>
                </form>
                )}
            </Well>
        </>
    );
}, (prev, next) => prev.selected?.id === next.selected?.id);